import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { useMediaQuery } from 'react-responsive';

import {
  HOUSEHOLD_LISTING_ARCHIVED_REASON_OTHER,
  HOUSEHOLD_LISTING_ARCHIVED_REASON_DENIED,
  HOUSEHOLD_LISTING_ARCHIVE_REASON_OPTION_ITEMS,
} from 'common/householdConstants';

import {
  hideFieldIf,
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import {
  MEDIA_QUERY_SMALL_SCREEN,
  MEDIA_QUERY_MEDIUM_SCREEN_850,
} from 'client/constants/mediaQueries';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrModalV2, { MODAL_THEME_FIXED_FOOTER_BUTTON } from 'client/components/SrModalV2/SrModalV2';
import SrButton, {
  SR_BUTTON_THEME_BLUR,
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_ORANGE_INVERTED,
} from 'client/components/SrButton/SrButton';
import SrFormikField, { SR_FIELD_THEME_PARTNER_FORM } from 'client/components/SrFormikField';

function getFormFields({ cancelReason }) {
  return [
    {
      id: 'cancelReason',
      type: 'select',
      properties: {
        enableResetOption: true,
        choices: HOUSEHOLD_LISTING_ARCHIVE_REASON_OPTION_ITEMS,
      },
      validations: ['required'],
    },
    hideFieldIf(cancelReason !== HOUSEHOLD_LISTING_ARCHIVED_REASON_DENIED, {
      id: 'rejectedForCreditReport',
      title: 'Are you denying the applicant because of information in their credit report?',
      type: 'yes_no',
      validations: ['required'],
      classnames: 'deny-title sunscreen partnerForm adobe',
    }),
    hideFieldIf(cancelReason !== HOUSEHOLD_LISTING_ARCHIVED_REASON_OTHER, {
      id: 'denyReason',
      title: 'Why are you archiving this application?',
      properties: {
        placeholder: 'Write archive reason here...',
      },
      type: 'long_text',
      validations: ['required'],
    }),
  ];
}

const SrArchiveAppModalSunScreen = ({ onClose, onArchive }) => {
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });
  const isMediumScreen = useMediaQuery({
    query: MEDIA_QUERY_MEDIUM_SCREEN_850,
  });

  const onSubmit = (formValues) => {
    onArchive(formValues);
  };

  return (
    <SrModalV2
      centerContent
      closeOnBackgroundClick
      showCloseButton={false}
      contentMaxWidth={!isSmallScreen ? '375px' : null}
      padding={isSmallScreen ? 'calc(100vh - 660px) 0 0 0' : '0 0 120px 0'}
      contentPadding="0"
      onClose={onClose}
      theme={isSmallScreen ? MODAL_THEME_FIXED_FOOTER_BUTTON : null}
      buttonTheme={isSmallScreen ? SR_BUTTON_THEME_ADOBE_GREY : SR_BUTTON_THEME_BLUR}
    >
      <SrBox
        borderRadius={isMediumScreen ? '14px' : '24px'}
        backgroundColor="white"
        boxShadow="0px 15px 26px rgba(212, 212, 212, 0.17)"
        boxSizing="border-box"
        maxWidth="375px"
        width={isSmallScreen ? 'calc(100vw - 20px)' : '375px'}
        padding="48px 40px 40px"
        margin={isMediumScreen ? '0 auto' : null}
      >
        <SrBox
          margin="0 auto"
          backgroundSize="cover"
          backgroundRepeat="no-repeat"
          backgroundPosition="center"
          backgroundImage="url(/images/warning-triangle-orange-large.svg)"
          width="120px"
          height="104px"
          position="relative"
          zIndex="5"
          marginBottom="18px"
        />
        <SrFlex justify="center" direction="col">
          <SrFlex marginBottom="16px" justify="center">
            <SrText
              color="blueGreycliff"
              font="greycliff"
              weight="800"
              fontSize="26px"
              lineHeight="33px"
              align="center"
            >
              Archive Application?
            </SrText>
          </SrFlex>
          <SrFlex marginBottom="18px" justify="center" direction="col">
            <SrText
              color="blueGreycliff"
              font="greycliff"
              weight="600"
              fontSize="16px"
              lineHeight="24px"
              align="center"
            >
              Sunroom will stop processing this application. If you choose to deny the applicant,
              Sunroom automatically notifies them and delivers an ‘adverse action notice’ if
              necessary.
            </SrText>
          </SrFlex>
          <SrBox>
            <Formik
              validate={(formValues) => {
                const formFields = getFormFields({ cancelReason: formValues.cancelReason });
                const errors = createFormValidationFn(formFields)(formValues);

                return errors;
              }}
              enableReinitialize
              initialValues={createInitialValues({
                formFields: getFormFields({
                  cancelReason: null,
                }),
              })}
              onSubmit={onSubmit}
              render={(formikBag) => {
                const formFields = getFormFields({
                  cancelReason: formikBag.values.cancelReason,
                });

                return (
                  <Form autoComplete="off">
                    <div>
                      {formFields.map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          formField={formField}
                          formikBag={formikBag}
                          className={formField.classnames || SR_FIELD_THEME_PARTNER_FORM}
                        />
                      ))}
                      <SrBox marginTop="18px" marginBottom="18px">
                        <SrButton type="submit" theme={SR_BUTTON_THEME_ADOBE_ORANGE_INVERTED}>
                          Archive
                        </SrButton>
                      </SrBox>
                      <SrButton theme={SR_BUTTON_THEME_ADOBE_GREY} onClick={onClose}>
                        Cancel
                      </SrButton>
                    </div>
                  </Form>
                );
              }}
            />
          </SrBox>
        </SrFlex>
      </SrBox>
    </SrModalV2>
  );
};

SrArchiveAppModalSunScreen.propTypes = {
  onClose: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
};

SrArchiveAppModalSunScreen.displayName = 'SrArchiveAppModalSunScreen';

export default SrArchiveAppModalSunScreen;
